import React from 'react'
import { Modal, ModalHeader, ModalBody, Row, Col, Badge, Card, CardBody } from 'reactstrap'
import './BatchDetailsModal.css'

const BatchDetailsModal = ({ isOpen, toggle, batch }) => {
    if (!batch) return null

    const formatCurrency = (value) => {
        return `$${Number(value || 0).toFixed(2)}`
    }

    const formatDateTime = (value) => {
        if (!value) return '--'
        return new Date(value).toLocaleString()
    }

    const getStatusColor = (status) => {
        const colorMap = {
            COMPLETED: 'success',
            RUNNING: 'info',
            PENDING: 'warning',
            CANCELED: 'secondary',
        }
        return colorMap[status] || 'secondary'
    }

    const renderDriverName = (result, className) => {
        const driverIdentifier = result.driverPk || result.driverId
        const driverLabel = result.driverName || `Driver #${driverIdentifier}`

        if (!result.driverId) {
            return <strong className={className}>{driverLabel}</strong>
        }

        return (
            <a
                className="bos-custom-link"
                href={`/driver/${result.driverId}/`}
                target="_blank"
                rel="noopener noreferrer"
                title={driverLabel}
            >
                <strong className={className}>{driverLabel}</strong>
            </a>
        )
    }

    const successfulResults = (batch.taskResults || []).filter((item) => item?.wasSuccessful)
    const failedResults = (batch.taskResults || []).filter((item) => !item?.wasSuccessful)

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" className="batch-details-modal">
            <ModalHeader>
                Batch #{batch.id} Details
            </ModalHeader>
            <ModalBody>
                {/* Batch Summary */}
                <Card className="mb-3 border-light shadow-sm">
                    <CardBody>
                        <Row>
                            <Col md={6}>
                                <div className="mb-2">
                                    <strong>Status:</strong>{' '}
                                    <Badge color={getStatusColor(batch.status)}>{batch.status}</Badge>
                                </div>
                                <div className="mb-2">
                                    <strong>Created:</strong> {formatDateTime(batch.scheduleDatetime || batch.dateAdded)}
                                </div>
                                <div className="mb-2">
                                    <strong>Completed:</strong> {formatDateTime(batch.completedAt)}
                                </div>
                                <div className="mb-2">
                                    <strong>Created By:</strong> {batch.createdByName || '--'}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="mb-2">
                                    <strong>Total Drivers:</strong> {batch.totalTasks}
                                </div>
                                <div className="mb-2">
                                    <strong>Processed:</strong> {batch.completedTasks}/{batch.totalTasks}
                                </div>
                                <div className="mb-2">
                                    <strong>Total Owed:</strong>{' '}
                                    <span className="text-danger">{formatCurrency(batch.totalAmountOwed)}</span>
                                </div>
                                <div className="mb-2">
                                    <strong>Total Collected:</strong>{' '}
                                    <span className="text-success">{formatCurrency(batch.totalAmountCollected)}</span>
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Results Section */}
                <Row>
                    {/* Successful Charges */}
                    <Col md={6}>
                        <h6 className="mb-3">
                            <Badge color="success" className="mr-2">
                                {successfulResults.length}
                            </Badge>
                            Successful Charges
                        </h6>
                        <div className="results-container">
                            {successfulResults.length > 0 ? (
                                successfulResults.map((result, idx) => (
                                    <Card
                                        key={`success-${result.driverId}-${idx}`}
                                        className="mb-2 border-success"
                                        style={{ borderLeft: '4px solid #28a745' }}
                                    >
                                        <CardBody className="py-2 px-3">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div>
                                                    {renderDriverName(result, 'text-success')}
                                                    <div className="text-muted small">ID: {result.driverPk || '--'}</div>
                                                </div>
                                                <Badge color="success" pill>
                                                    ✓
                                                </Badge>
                                            </div>
                                            <div className="small">
                                                <div>
                                                    <strong>Charged:</strong>{' '}
                                                    <span className="text-success">
                                                        {formatCurrency(result.amountCollected)}
                                                    </span>
                                                </div>
                                                <div className="text-muted">
                                                    Attempted: {formatCurrency(result.amountAttempted)}
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))
                            ) : (
                                <div className="text-muted text-center py-3">
                                    No successful charges recorded.
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Failed Charges */}
                    <Col md={6}>
                        <h6 className="mb-3">
                            <Badge color="danger" className="mr-2">
                                {failedResults.length}
                            </Badge>
                            Failed Charges
                        </h6>
                        <div className="results-container">
                            {failedResults.length > 0 ? (
                                failedResults.map((result, idx) => (
                                    <Card
                                        key={`failed-${result.driverId}-${idx}`}
                                        className="mb-2 border-danger"
                                        style={{ borderLeft: '4px solid #dc3545' }}
                                    >
                                        <CardBody className="py-2 px-3">
                                            <div className="d-flex justify-content-between align-items-start mb-1">
                                                <div>
                                                    {renderDriverName(result, 'text-danger')}
                                                    <div className="text-muted small">ID: {result.driverPk || '--'}</div>
                                                </div>
                                                <Badge color="danger" pill>
                                                    ✗
                                                </Badge>
                                            </div>
                                            <div className="small">
                                                <div className="text-muted">
                                                    Attempted: {formatCurrency(result.amountAttempted)}
                                                </div>
                                                <div className="text-danger mt-1">
                                                    <strong>Reason:</strong>{' '}
                                                    {result.errorMessage || 'Charge did not succeed.'}
                                                </div>
                                            </div>
                                        </CardBody>
                                    </Card>
                                ))
                            ) : (
                                <div className="text-muted text-center py-3">
                                    No failed charges recorded.
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </ModalBody>
        </Modal>
    )
}

export default BatchDetailsModal
